import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import type { OperationVariables, TypedDocumentNode } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";

import type {
  BaseSummariesQueryResult,
  SummariesFormattersGroups,
} from "./types";
import { addFooterRecursively } from "./utils";

export const useDataTableSummaries = <
  TData,
  Q extends BaseSummariesQueryResult,
  V extends OperationVariables,
>(
  columnDefs: Array<ColumnDef<TData>>,
  summariesDocument: TypedDocumentNode<Q, V>,
  options: useQuery.Options<Q, V>,
  roundingMap: SummariesFormattersGroups,
) => {
  const summariesResult = useQuery(summariesDocument, {
    ...options,
    variables: {
      ...options.variables,
    },
  });

  return useMemo(() => {
    if (summariesResult.loading) {
      return {
        summariesResult,
        columns: addFooterRecursively(columnDefs, undefined, undefined, {
          system: {},
          selection: {},
        }),
      };
    }

    if (!summariesResult.data?.selectionSummaries) {
      return {
        summariesResult,
        columns: columnDefs,
      };
    }

    return {
      summariesResult,
      columns: addFooterRecursively(
        columnDefs,
        summariesResult.data.selectionSummaries,
        summariesResult.data.systemSummaries,
        roundingMap,
      ),
    };
  }, [columnDefs, summariesResult, roundingMap]);
};
